import { CheckboxWithLabel } from "app/components/Checkbox/CheckboxWithLabel";
import Collapsible from "app/components/Collapsible/Collapsible";
import InputWithLabel from "app/components/FormInputs/components/InputWithLabel/InputWithLabel";
import Recaptcha from "app/screens/AuthScreen/components/Recaptcha/Recaptcha";
import { LossLimitsType } from "app/types/userType";
import {
  ChangeEventHandler,
  MouseEventHandler,
  useEffect,
  useState,
} from "react";
import { FormattedMessage, IntlShape } from "react-intl";
import HeardAboutUs from "../HeardAboutUs/HeardAboutUs";
import LossLimits from "../LossLimits/LossLimits";
import IdentityDocumentationText from "./components/IdentityDocumentationText/IdentityDocumentationText";
import MinimumDepositText from "./components/MinimumDepositText/MinimumDepositText";

type Props = {
  handleChange: (id: string, value: boolean) => void;
  active: boolean;
  currencyId: number;
  currencies: any;
  handleCurrencyChange: ChangeEventHandler;
  onSubmit: MouseEventHandler;
  handleCheckboxToggle: ChangeEventHandler;
  recaptchaError: string;
  loading: boolean;
  errorMessage: string;
  handleHeardAboutUsChange: Function;
  consentToMarketing: boolean;
  betRequirementsAccepted: boolean;
  ageDocumentationAccepted: boolean;
  promoCode: string;
  intl: IntlShape;
  heardAboutUsOther: string;
  heardAboutUs: string;
  handleExpire: Function;
  handleVerify: Function;
  lossLimits: LossLimitsType;
  handleLimitChange: (id: string, limit: LossLimitsType) => void;
};

const AccountCurrency: React.FC<Props> = props => {
  const {
    handleChange,
    active,
    currencyId,
    currencies,
    handleCurrencyChange,
    onSubmit,
    handleCheckboxToggle,
    recaptchaError,
    loading,
    errorMessage,
    handleLimitChange,
  } = props;

  const [selectedCurrencyId, setSelectedCurrencyId] = useState(currencyId || 0);
  const [hasPromoCode, setHasPromoCode] = useState(false);

  useEffect(() => {
    setSelectedCurrencyId(currencyId || 0);
  }, [currencyId]);

  return (
    <Collapsible
      onToggle={() => {
        handleChange("accountCurrencyActive", !active);
      }}
      header={
        <div className="collapsible-header">
          <FormattedMessage
            id="register-account-settings"
            defaultMessage="Account Settings"
          />
        </div>
      }
      content={
        <div className="collapsible-content collapsible-content--currency">
          <div className="register-screen__settings-top">
            <div className="register-screen__form__dropdown">
              <label className="text-input__label" htmlFor="currency">
                <FormattedMessage tagName="span" id="settings-currency" />
                {"*"}
              </label>
              <select
                className="form-control"
                value={selectedCurrencyId}
                onChange={e => {
                  setSelectedCurrencyId(Number(e.target.value));
                  handleCurrencyChange(e);
                }}
                id="currency"
              >
                {!selectedCurrencyId && (
                  <option value={0}>
                    <FormattedMessage id="home-register-select-currency" />
                  </option>
                )}
                {currencies &&
                  currencies.map((currency, index) => (
                    <option key={index} value={currency.id}>
                      {currency.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* show feature until QA have tested, then hide until the rest of the feature is ready */}
            {true && (
              <LossLimits
                onLimitChange={handleLimitChange}
                limit={props.lossLimits}
                handleErrorChange={change => {
                  console.log("change: ", change);
                  handleChange(change);
                }}
              />
            )}
          </div>
          <div className="register-screen__settings-bottom">
            <div className="register-screen__heard-about-us-container">
              <HeardAboutUs
                handleHeardAboutUsChange={props.handleHeardAboutUsChange}
                heardAboutUs={props.heardAboutUs}
                handleChange={handleChange}
                heardAboutUsOther={props.heardAboutUsOther}
              />
            </div>
            <div className="register-screen__form__checkboxes">
              <div className="register-screen__promo-container">
                <div className="register-screen__form__checkbox">
                  <CheckboxWithLabel
                    checked={hasPromoCode}
                    onChange={() => {
                      setHasPromoCode(!hasPromoCode);
                    }}
                    disabled={false}
                    labelText={
                      <FormattedMessage id="registration-form-promoCode" />
                    }
                    id={"hasPromoCode"}
                  />
                </div>
                <div className="text-input-container ">
                  <InputWithLabel
                    label={""}
                    inputId="promoCode"
                    value={props.promoCode}
                    onValueChange={handleChange}
                    maxLength={20}
                    disabled={!hasPromoCode}
                  />
                </div>
              </div>
              <div className="register-screen__form__checkbox">
                <CheckboxWithLabel
                  checked={props.ageDocumentationAccepted}
                  onChange={handleCheckboxToggle}
                  disabled={false}
                  labelText={<IdentityDocumentationText />}
                  id={"ageDocumentationAccepted"}
                />
              </div>
              <div className="register-screen__form__checkbox">
                <CheckboxWithLabel
                  checked={props.betRequirementsAccepted}
                  onChange={handleCheckboxToggle}
                  disabled={false}
                  labelText={<MinimumDepositText />}
                  id={"betRequirementsAccepted"}
                />
              </div>
              <div className="register-screen__form__checkbox">
                <CheckboxWithLabel
                  checked={props.consentToMarketing}
                  onChange={handleCheckboxToggle}
                  disabled={false}
                  labelText={<FormattedMessage id="consent-to-marketing" />}
                  id={"consentToMarketing"}
                />
              </div>
            </div>
          </div>

          <div className="register-screen__form__recaptcha">
            <Recaptcha
              onVerify={props.handleVerify}
              onExpire={props.handleExpire}
            />
          </div>
          <div className="register-screen__form__error">{errorMessage}</div>
          <button
            type="submit"
            className={`register-screen__form__submit-button ${
              loading && "register-screen__form__submit-button--disabled"
            }`}
            id="register-screen__form__submit-button"
            onClick={onSubmit}
          >
            <FormattedMessage tagName="span" id="home-create-account" />
          </button>
        </div>
      }
      isActive={active}
      withArrows
    />
  );
};

export default AccountCurrency;
